import { Component } from '@angular/core';
import {
  NavigationLocation,
  NextPageService,
  PageLayout,
  SearchType,
  SliderType,
} from '@cvx/nextpage';
import { combineLatest, map } from 'rxjs';
import { UserService } from './core/services/user.service';
import { Constants } from './shared/constants/constants';
import { EsraPipingAPIClient } from './shared/models/autogenerated-piping';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [EsraPipingAPIClient],
    standalone: false
})
export class AppComponent {
  title = 'ESRA';
  name = '';
  loggedin: boolean;
  loginDisplay = false;
  errConnectionRefused: boolean;

  NavigationLocation = NavigationLocation;
  PageLayout = PageLayout;
  SearchType = SearchType;
  SliderType = SliderType;

  constructor(
    private readonly NP: NextPageService,
    private readonly userService: UserService
  ) {
    this.NP.options.sitename =
      'Engineering Standards Repository Application (ESRA)';
    this.prepareNavigationMenuItems();
  }

  handleErrConnectionRefused(event: boolean) {
    this.errConnectionRefused = event;
  }

  prepareNavigationMenuItems() {
    this.prepareHomeMenuItems();
    this.prepareCoatingMenuItems();
    this.prepareWeldingProcedureMenuItems();
    this.preparePipingAndValvesMenuItems();
    this.prepareAdministrationMenuItems();
  }

  prepareHomeMenuItems() {
    const homeMenuItems = {
      title: 'home',
      url: '/',
    };
    this.NP.options.navigationItems.push(homeMenuItems);
  }

  prepareCoatingMenuItems() {
    const coatingMenuChildItems = [
      {
        title: 'search',
        url: '/coatingsolutionsearch',
      },
      {
        title: 'my workspace',
        url: '/myworkspace?technology=coatings',
      },
      {
        title: 'revisions',
        url: '/allrevisions',
      },
    ];

    combineLatest<[boolean, boolean]>([
      this.userService.isRoleExistsObservable(
        Constants.coatingSpecificationExport
      ),
      this.userService.isRoleExistsObservable(Constants.scanner),
    ])
      .pipe(
        map(([allowSpecExport, isScanner]) => {
          if (allowSpecExport || isScanner) {
            coatingMenuChildItems.push({
              title: 'spec export',
              url: '/spec-export',
            });
          }
        })
      )
      .subscribe();

    const coatingMenuItems = {
      title: 'coatings',
      url: '/coatingsolutionsearch',
      children: coatingMenuChildItems,
    };
    this.NP.options.navigationItems.push(coatingMenuItems);
  }

  prepareWeldingProcedureMenuItems() {
    const weldingProcedureMenus = {
      title: 'welding procedures',
      url: '/weldingsearch',
      children: [
        {
          title: 'search',
          url: '/weldingsearch',
        },
        {
          title: 'my workspace',
          url: '/myworkspace?technology=welding',
        },
        {
          title: 'revisions',
          url: '/weldingrevision/all',
        },
      ],
    };
    this.NP.options.navigationItems.push(weldingProcedureMenus);
  }

  preparePipingAndValvesMenuItems() {
    const menuItems = [
      {
        title: 'search',
        url: '/piping/search',
      },
      {
        title: 'my workspace',
        url: '/myworkspace?technology=piping',
      },
    ];


    combineLatest<[boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean]>([
      this.userService.isPipingAndValvesAdminObservable(),
      this.userService.isRoleExistsObservable(Constants.scanner),
      this.userService.isRoleExistsObservable(Constants.valvesBulkUploadReader),
      this.userService.isRoleExistsObservable(Constants.valvesBulkUploadWriter),
      this.userService.isRoleExistsObservable(Constants.pipingRevisionReader),
      this.userService.isRoleExistsObservable(Constants.admin),
      this.userService.isRoleExistsObservable(Constants.pipingSasbuAdmin),
      this.userService.isRoleExistsObservable(Constants.pipingNmaAdmin),
    ])
      .pipe(
        map(([enableAdminMenu, isScanner, isValveBulkReader, isValveBulkWriter, isRevisionReader, isAdmin, isSasbuAdmin, isNmaAdmin]) => {
          if (enableAdminMenu || isScanner) {
            menuItems.push(
              {
                title: 'admin search',
                url: '/piping/adminSearch',
              },
              {
                title: 'dictionaries',
                url: '/piping/dictionaries',
              }
            );
          }
          if (isRevisionReader) {
            menuItems.push(
              {
                title: 'revisions and releases',
                url: '/piping/revisionsAndReleases',
              });
          }

          if (isValveBulkReader || isValveBulkWriter) {
            menuItems.push({
              title: 'valve bulk upload',
              url: '/piping/valveBulkUpload',
            });
          }

          if (isAdmin || isSasbuAdmin || isNmaAdmin) {
            menuItems.push({
              title: 'Pipe Class & Valve Import',
              url: '/piping/buPdfBulkUpload',
            });
          }
        })
      )
      .subscribe();

    const pipingAndValvesMenuItems = {
      title: 'piping and valves',
      url: '/piping/search',
      children: menuItems,
    };
    this.NP.options.navigationItems.push(pipingAndValvesMenuItems);
  }

  prepareAdministrationMenuItems() {
    combineLatest<[boolean, boolean]>([
      this.userService.isRoleExistsObservable(Constants.admin),
      this.userService.isRoleExistsObservable(Constants.pipingDictionaryWriter),
    ])
      .pipe(
        map(([isAdmin, isDictionaryWriter]) => {
          if (!isAdmin && !isDictionaryWriter) {
            return;
          }

          const administrationMenuChildItems = [
            {
              title: 'consolidation mapping',
              url: '/administration/consolidation-mapping',
            },
          ];

          // User management is Admin-only -- dictionary writers manage piping
          // dictionaries but must not view or edit user-to-business-unit access.
          if (isAdmin) {
            administrationMenuChildItems.push({
              title: 'user management',
              url: '/administration/user-management',
            });
          }

          this.NP.options.navigationItems.push({
            title: 'administration',
            url: administrationMenuChildItems[0].url,
            children: administrationMenuChildItems,
          });
        })
      )
      .subscribe();
  }

  readonly currentUserProfile$ = this.userService.currentUserProfile$;
}
